// A printable call sheet: one page per salesman, the customers to call with why,
// their usual pattern, phone numbers and room for notes. Prints once when it mounts;
// the parent mounts it only for a print request and removes it after printing.
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { money, shortDate } from "./format";
import { cadence, daysAgo, phoneText } from "./customerText";
import "./CallSheet.css";

const OFFICE = "Office / Unassigned";

function groupBySalesman(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const name = row.salesman?.name || OFFICE;
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(row);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === OFFICE) - (b === OFFICE) || a.localeCompare(b))
    .map(([name, list]) => ({ name, rows: list }));
}

export default function CallSheet({ title, asOf, scope, rows, onDone }) {
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const finished = () => done.current?.();
    window.addEventListener("afterprint", finished);
    // Let the sheet render before the print dialog opens.
    const timer = setTimeout(() => {
      try { window.print(); } catch (error) { finished(); }
    }, 50);
    return () => { clearTimeout(timer); window.removeEventListener("afterprint", finished); };
  }, []);

  return createPortal(<div className="sa-call-sheet">
    {groupBySalesman(rows).map((group) => {
      const atRisk = group.rows.reduce((sum, row) => sum + Number(row.at_risk_weekly || 0), 0);
      return <section className="sa-call-group" key={group.name}>
        <header>
          <h1>Call sheet · {group.name}</h1>
          <p>{title} · {group.rows.length} {group.rows.length === 1 ? "customer" : "customers"} · {money(atRisk)} a week at risk</p>
          <p>As of {shortDate(asOf, { year: true })}{scope ? ` · ${scope}` : ""}</p>
        </header>
        <table>
          <thead><tr><th>Customer</th><th>Why</th><th>Usually</th><th>Last order</th><th>Notes</th></tr></thead>
          <tbody>{group.rows.map((row) => <tr key={row.key}>
            <td><strong>{row.name}</strong>{(row.phones || []).map((entry) => <span key={entry.phone}>{phoneText(entry.phone)}{entry.note ? ` (${entry.note})` : ""}</span>)}</td>
            <td><strong>{row.status_label}</strong>{(row.reasons || []).filter((reason) => reason.type !== "switching")
              .map((reason) => <span key={reason.type}>{reason.detail}</span>)}
              {[...(row.dropped_categories || []), ...(row.dropped_products || [])].length > 0 &&
                <span>Stopped or cut back: {[...(row.dropped_categories || []), ...(row.dropped_products || [])].slice(0, 4).map((item) => item.name).join(", ")}</span>}</td>
            <td>{money(row.baseline_weekly)}/wk, {cadence(row.typical_gap_days)}<span>Last 4 weeks {money(row.recent_weekly)}/wk</span><span>{money(row.at_risk_weekly)}/wk at risk</span></td>
            <td>{shortDate(row.last_order_date)}<span>{daysAgo(row.days_since_last_order)}</span></td>
            <td className="sa-call-notes"><span>☐ Called</span><span>☐ Reached</span><span>☐ Ordered</span></td>
          </tr>)}</tbody>
        </table>
      </section>;
    })}
  </div>, document.body);
}
